import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { animated, useSpring } from '@react-spring/web';
import axios from 'axios';
import { URLS } from '../config';
import { custom } from '../custom';
import { theme } from '../constants';
import { actions } from '../store/actions';
import { items as ShopItem } from '../items';
import { IoIosArrowDown } from 'react-icons/io';
import { svg } from '../assets/svg';
import { toast } from 'react-toastify';
import { logoutHandler } from '../services/logoutHandler';
import { Switch, Select, Modal } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { useSelector, TypedUseSelectorHook } from 'react-redux';
import type { RootState } from '../hooks';
import { useAppDispatch, useAppNavigate, useAppSelector } from '../hooks';

const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

const sortingBy = [
  { id: 1, title: 'جدیدترین', value: 'created_time' },
  { id: 2, title: 'ارزان به گران', value: 'price' },
  { id: 3, title: 'گران به ارزان', value: '-price' },
  { id: 4, title: 'پرفروشترین', value: '-sales' },
];

export const Shop: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [ProductData, setProductData] = useState<any[]>([]);
  const [sort, setSort] = useState(sortingBy[0]);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAvailable, setIsAvailable] = useState(false);

  const [selectedBrands, setSelectedBrands] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const token = useTypedSelector(state => state.auth.token);
  const location = useLocation();
  const incomingFilters = location.state?.filters;
  const brands = useTypedSelector(state => state.filtersList.brands);
  const categories = useTypedSelector((state) => state.filtersList.categories);

  const limit = 10;

  /**  گرفتن دیتا */
// تغییر: getData می‌تونه فیلترها رو مستقیم بگیره
const getData = async (
  reset = false,
  overrideFilters?: {
    brand?: number | null;
    category?: number | null;
    isAvailable?: boolean | null;
    searchQuery?: string | null;
    sortValue?: string | null;
  }
) => {
  if (!hasMore && !reset) return;

  const params: any = {
    limit,
    offset: reset ? 0 : offset,
  };

  const brandToUse = overrideFilters?.brand ?? selectedBrands;
  const categoryToUse = overrideFilters?.category ?? selectedCategory;
  const availableToUse = overrideFilters?.isAvailable ?? isAvailable;
  const searchToUse = overrideFilters?.searchQuery ?? searchQuery;
  const sortToUse = overrideFilters?.sortValue ?? sort?.value;

  if (brandToUse) params.brand = brandToUse;
  if (categoryToUse) params.category = categoryToUse;
  if (availableToUse) params.is_available = true;
  if (searchToUse?.trim()) params.search = searchToUse.trim();
  if (sortToUse) params.ordering = sortToUse;

  const queryString = new URLSearchParams(params).toString();
  setLoading(true);

  try {
    const response = await axios.get(`${URLS.GET_PRODUCTS}?${queryString}`, {
      headers: { Authorization: `JWT ${token}` },
    });

    const newProducts = response.data.results;
    setProductData((prev) => (reset ? newProducts : [...prev, ...newProducts]));

    if (newProducts.length < limit) {
      setHasMore(false);
    } else {
      setOffset((prev) => (reset ? limit : prev + limit));
    }
  } catch (error: any) {
    if (error.response?.status === 401) {
      toast.error("برای ادامه لطفاً وارد حساب کاربری شوید");
      logoutHandler(dispatch, navigate);
    } else {
      toast.error("خطا در دریافت اطلاعات");
    }
  } finally {
    setLoading(false);
  }
};


useEffect(() => {
  const incomingFilters = location.state?.filters;
  if (incomingFilters) {
    const brand = incomingFilters.brand || null;
    const category = incomingFilters.category || null;

    setSelectedBrands(brand);
    setSelectedCategory(category);

    setOffset(0);
    setHasMore(true);
    setProductData([]);

    // 🚀 مستقیم پاس میدیم به getData
    getData(true, {
      brand,
      isAvailable,
      category,
    });
  }
}, [location.state?.filters]);
  

useEffect(() => {
  setOffset(0);
  setHasMore(true);
  setProductData([]);

  getData(true, {
    brand: selectedBrands,
    category: selectedCategory,
    isAvailable,
    searchQuery,
    sortValue: sort?.value,
  });

  window.scrollTo(0, 0);
}, [selectedBrands, selectedCategory, isAvailable, sort, searchQuery]);

  /** گرفتن لیست فیلترها (برند + دسته‌بندی) */
  useEffect(() => {
    if (!brands || !categories) {
      axios
        .get(URLS.GET_ALL_FILTERS, { headers: { Authorization: `JWT ${token}` } })
        .then((res) => {
          dispatch(actions.setFiltersList(res.data));
        })
        .catch(() => {
          toast.error("خطا در دریافت فیلترها");
        });
    }
  }, []);

  const handleAvailableChange = (checked: boolean) => {
    setIsAvailable(checked);
  };

  const { selectedSizes, selectedCategories, selectedTags } =
    useAppSelector((state: RootState) => state.filterSlice);

  const cart = useAppSelector((state: RootState) => state.cartSlice.list);
  const total = useAppSelector(
    (state: RootState) => state.cartSlice.payAmount,
  );

  const filteredProducts = ProductData.filter((product: any) => {
    const sizeMatch =
      selectedSizes.length === 0
        ? true
        : selectedSizes.some(size => product.sizes.includes(size));

    const tagMatch =
      selectedTags.length === 0
        ? true
        : selectedTags.some(tag => product.tags.includes(tag));

    const statusMatch =
      selectedCategories.length === 0
        ? true
        : (product.isNew && selectedCategories.includes('جدید')) ||
          (product.isTop && selectedCategories.includes('پرفروش‌ترین')) ||
          (product.oldPrice && selectedCategories.includes('فروش'));

    return statusMatch && tagMatch && sizeMatch;
  });

  const sortedProducts = [...filteredProducts]; // سرور مرتب‌سازی رو هندل میکنه

  const handleOnClick = () => {
    if (cart.length > 0) {
      dispatch(actions.setScreen('Order'));
      navigate('/home');
      return;
    }
    return alert('سبد خرید شما خالی است');
  };

  const backdropAnimation = useSpring({
    opacity: modalIsOpen ? 1 : 0,
    display: modalIsOpen ? 'flex' : 'none',
  });

  useEffect(() => {
    document.body.style.overflow = modalIsOpen ? 'hidden' : 'auto';
  }, [modalIsOpen]);

  /** Header */
  const renderHeader = () => {
    return (
      <header
        style={{
          top: 0,
          position: 'sticky',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.colors.white,
          zIndex: 4,
        }}
      >
        {/* اطلاعات بالا */}
        <div
          style={{
            height: 52,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            style={{
              position: 'absolute',
              right: 0,
              padding: 20,
              cursor: 'pointer',
              backgroundColor: 'transparent',
              border: 'none',
              rotate: '180deg'
            }}
            onClick={() => navigate(-1)}
          >
            <svg.GoBackSvg />
          </button>

          <input
            type="text"
            placeholder="جستجو کنید..."
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              border: `1px solid ${theme.colors.aliceBlue2}`,
              maxWidth: 300,
              fontSize: 14,
              width: 'calc(100% - 100px)',
              textAlign: 'center',
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              ...theme.fonts.Mulish_600SemiBold,
            }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setOffset(0);
                setHasMore(true);
                setProductData([]);
                getData(true);
              }
            }}
          />

          <button
            onClick={handleOnClick}
            style={{
              position: 'absolute',
              left: 0,
              paddingTop: 12,
              paddingBottom: 12,
              paddingLeft: 20,
              paddingRight: 20,
              borderRadius: 12,
            }}
          >
            <svg.BasketSvg />
            <span
              style={{
                paddingLeft: 6,
                paddingRight: 6,
                borderRadius: 10,
                left: 35,
                bottom: 12,
                position: 'absolute',
                display: 'flex',
                paddingTop: 2,
                paddingBottom: 2.8,
                fontSize: 10,
                color: theme.colors.white,
                ...theme.fonts.Mulish_700Bold,
                backgroundColor: theme.colors.coralRed,
              }}
            >
              <div>
                <span style={{ marginLeft: '5px' }}>
                  {cart.length > 0 ? total.toFixed(2) : 0}
                </span>
                <span>تومان</span>
              </div>
            </span>
          </button>
        </div>

        {/* فیلترها و مرتب‌سازی */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 20px',
            gap: '1rem',
            paddingBottom: 19,
          }}
        >
          <button
            onClick={() => setIsFilterModalOpen(true)}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <FilterOutlined style={{ color: '#60708e', marginLeft: 4 }} />
            <span style={{ fontSize: '15px', color: '#60708e' }}>فیلترها</span>
          </button>

          <button
            style={{ display: 'flex', alignItems: 'center' }}
            onClick={() => setIsOpen(true)}
          >
            <span style={{ fontSize: '15px', color: '#60708e', marginLeft: '6px' }}>
              مرتب‌سازی: {sort?.title}
            </span>
            <IoIosArrowDown color="#60708e" />
          </button>
        </div>
      </header>
    );
  };

  /** Content */
  const renderContent = () => {
    return (
      <div>
        <custom.InfiniteScrollR
          data={ProductData}
          next={getData}
          hasMore={hasMore}
          loading={loading}
          endMessage={true}
        >
          <section
            className="mx-auto grid grid-cols-1 justify-items-center gap-y-3 gap-x-3 px-4 sm:px-6 md:px-8 mt-2 mb-1"
          >
            {sortedProducts.map((reviewItem: any, index: any, array: any) => {
              const isLast = index === array.length - 1;
              return (
                <ShopItem.ProductCard
                  key={reviewItem.id}
                  isLast={isLast}
                  item={reviewItem}
                  version={1}
                />
              );
            })}
          </section>
        </custom.InfiniteScrollR>
      </div>
    );
  };

  /**  Modal مرتب‌سازی */
  const renderModal = (): JSX.Element => {
    return (
      <animated.div
        className='container'
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(25, 51, 100, 0.6)',
          position: 'fixed',
          inset: 0,
          zIndex: 22,
          overflow: 'hidden',
          justifyContent: 'center',
          alignItems: 'center',
          ...backdropAnimation,
        }}
        onClick={() => setIsOpen(false)}
      >
        <div
          style={{
            width: 'calc(100% - 80px)',
            backgroundColor: 'white',
            marginLeft: 20,
            marginRight: 20,
            borderRadius: 5,
            paddingTop: 10,
            paddingLeft: 20,
          }}
        >
          {sortingBy.map((item) => {
            return (
              <button
                key={item.id}
                style={{
                  width: '100%',
                  borderBottom: '1px solid #E5E5E5',
                  padding: '10px 20px 15px 20px',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setSort(item);
                  setIsOpen(false);
                }}
              >
                <span
                  style={{
                    ...theme.fonts.Mulish_400Regular,
                    fontSize: 15,
                    color: theme.colors.textColor,
                    lineHeight: 1.7,
                  }}
                >
                  {item.title}
                </span>
                <div
                  style={{
                    width: 17,
                    height: 17,
                    border: '3px solid #E8EFF4',
                    borderRadius: 50,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {sort.id === item.id && (
                    <div
                      style={{
                        width: 9,
                        height: 9,
                        backgroundColor: theme.colors.mainColor,
                        borderRadius: 50,
                      }}
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </animated.div>
    );
  };

  /** 📌 Modal فیلتر */
  const renderFilterModal = (): JSX.Element => {
    return (
      <Modal
        title="فیلتر محصولات"
        open={isFilterModalOpen}
        onCancel={() => setIsFilterModalOpen(false)}
        footer={null}
      >
        <div className="flex flex-col gap-4">
          {/* فیلتر موجود */}
          <div className="flex items-center gap-2">
            <span style={{ fontSize: '14px', color: '#60708e' }}>موجود</span>
            <Switch
              checkedChildren="موجود"
              unCheckedChildren="همه"
              checked={isAvailable}
              onChange={handleAvailableChange}
              size="small"
            />
          </div>

          {/* فیلتر برند */}
          <div className="flex items-center gap-2">
            <span style={{ fontSize: '14px', color: '#60708e' }}>برند:</span>
            <Select
              showSearch
              allowClear
              placeholder="انتخاب برند"
              value={selectedBrands}
              onChange={(value)=> setSelectedBrands(value)}
              style={{ minWidth: 200 }}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
              options={(brands ?? []).map((brand) => ({
                label: brand.title,
                value: brand.id,
              }))}
            />
          </div>

          {/* فیلتر دسته‌بندی */}
          <div className="flex items-center gap-2">
            <span style={{ fontSize: '14px', color: '#60708e' }}>دسته‌بندی:</span>
            <Select
              showSearch
              allowClear
              placeholder="انتخاب دسته‌بندی"
              value={selectedCategory}
              onChange={(value)=> setSelectedCategory(value)}
              style={{ minWidth: 200 }}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
              options={(categories ?? []).map((category) => ({
                label: category.name,
                value: category.id,
              }))}
            />
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <>
      {renderHeader()}
      {renderContent()}
      {renderModal()}
      {renderFilterModal()}
    </>
  );
};
